<template>
  <div class="page-container">
    <!-- 使用说明区域 -->
    <Card dis-hover class="usage-guide-section">
      <div slot="title" class="section-title">
        <Icon type="ios-information-circle" size="20" />
        <span>资源点使用说明</span>
      </div>
      <Collapse v-model="guideCollapse" simple>
        <Panel name="1">
          <span class="guide-panel-title">计费规则</span>
          <div slot="content" class="guide-content">
            <div class="guide-item">
              <div class="guide-item-title">
                <Icon type="ios-calculator" size="16" />
                <span>核心换算规则</span>
              </div>
              <ul class="guide-list">
                <li><strong>中文字符计费：</strong>1个中文字符 = 0.5个资源点（100万资源点 = 200万个中文字符）</li>
                <li><strong>非中文字符计费：</strong>1个非中文字符（英文、数字、符号等）= 0.25个资源点</li>
                <li><strong>计费范围：</strong>统计AI服务的<strong>输入文本</strong>和<strong>输出结果</strong>的字符总数（包括标点符号、空格及换行符），<strong>输入和输出统一按照上述标准计费</strong></li>
                <li><strong>扣费方式：</strong>使用服务时实时计算字符消耗量，按上述比例从账户资源点余额中抵扣</li>
                <li><strong>计费说明：</strong>无论是您发送给AI的输入内容，还是AI返回给您的输出内容，都按照相同的计费标准进行统计和扣费</li>
              </ul>
            </div>
            <div class="guide-item">
              <div class="guide-item-title">
                <Icon type="ios-time" size="16" />
                <span>使用注意事项</span>
              </div>
              <ul class="guide-list">
                <li><strong>资源点有效期：</strong>购买后12个月内有效，过期未使用的资源点自动失效，不予退还</li>
                <li><strong>超额计费：</strong>套餐资源点用尽后，可直接购买新套餐或按按量计费模式（同套餐换算比例）实时扣费</li>
                <li><strong>查询与对账：</strong>支持在账户中心查看资源点余额、字符消耗明细及历史账单，数据保留6个月</li>
                <li><strong>精度说明：</strong>字符统计精确到个位，不足1个字符按1个字符计费，资源点抵扣按0.5的整数倍计算</li>
              </ul>
            </div>
            <div class="guide-example">
              <div class="example-title">
                <Icon type="ios-bulb" size="16" />
                <span>示例说明</span>
              </div>
              <div class="example-content">
                <p>假设您发送了100个中文字符的输入，AI返回了200个中文字符的输出：</p>
                <p class="example-calculation">
                  输入字符数：100个中文字符<br>
                  输出字符数：200个中文字符<br>
                  总字符数 = 100 + 200 = 300个中文字符<br>
                  消耗资源点 = 300 × 0.5 = 150个资源点
                </p>
                <p style="margin-top: 12px; color: #808695; font-size: 13px;">
                  <Icon type="ios-information-circle-outline" size="14" />
                  说明：输入和输出统一按照中文字符0.5资源点/字符的标准计费
                </p>
              </div>
            </div>
          </div>
        </Panel>
      </Collapse>
    </Card>

    <!-- 套餐选择区域 -->
    <Card dis-hover class="package-section">
      <div slot="title" class="section-title">
        <Icon type="ios-cube" size="20" />
        <span>选择套餐</span>
      </div>
      <Spin fix v-if="loading" />
      <Row :gutter="16">
        <Col v-for="item in packages" :key="item.id" :xl="6" :lg="6" :md="8" :sm="12" :xs="24">
          <div 
            class="package-card" 
            :class="{ 'package-card-selected': selectedPackage && selectedPackage.id === item.id }"
            @click="selectPackage(item)"
          >
            <div v-if="selectedPackage && selectedPackage.id === item.id" class="package-selected-badge">
              <Icon type="ios-checkmark-circle" size="20" />
              <span>已选择</span>
            </div>
            <div class="package-content">
              <div class="package-name">{{ item.name }}</div>
              <div class="package-price-wrapper">
                <span class="price-symbol">￥</span>
                <span class="price-value">{{ formatPrice(item.price) }}</span>
              </div>
              <div class="package-points-wrapper">
                <Icon type="ios-flash" size="18" color="#2d8cf0" />
                <span class="points-label">{{ formatNumber(item.base_points + (item.bonus_points || 0)) }}</span>
                <span class="points-unit">资源点</span>
              </div>
              <div class="package-validity">
                <Icon type="ios-time-outline" size="14" />
                <span>有效期 {{ item.valid_days }} 天</span>
              </div>
            </div>
          </div>
        </Col>
        <Col v-if="!loading && packages.length === 0" span="24">
          <Alert type="warning" show-icon>暂无可用套餐，请联系管理员配置。</Alert>
        </Col>
      </Row>
    </Card>

    <!-- 订单详情区域 -->
    <Card v-if="selectedPackage" dis-hover class="order-section">
      <div slot="title" class="section-title">
        <Icon type="ios-cart" size="20" />
        <span>订单详情</span>
      </div>
      <Form :label-width="120" label-colon>
        <FormItem label="已选套餐">
          <div class="selected-package-info">
            <span class="package-name">{{ selectedPackage.name }}</span>
            <span class="package-desc">{{ selectedPackage.description || 'AI资源点套餐' }}</span>
          </div>
        </FormItem>
        <FormItem label="购买数量">
          <InputNumber 
            v-model="orderForm.quantity" 
            :min="1" 
            :max="99"
            :precision="0"
            style="width: 200px;"
            @on-change="calculatePrice"
          />
          <span style="margin-left: 12px; color: #808695;">份</span>
        </FormItem>
        <FormItem label="支付方式">
          <RadioGroup v-model="orderForm.pay_type" @on-change="calculatePrice">
            <Radio :label="1" border>
              <Icon type="logo-wechat" size="18" style="margin-right: 6px;" />
              <span>微信支付</span>
            </Radio>
          </RadioGroup>
        </FormItem>
        <FormItem label="资源点详情">
          <div class="points-detail">
            <div class="detail-row">
              <span class="detail-label">单份基础资源点：</span>
              <span class="detail-value">{{ formatNumber(selectedPackage.base_points) }} 点</span>
            </div>
            <div class="detail-row" v-if="selectedPackage.bonus_points">
              <span class="detail-label">单份赠送资源点：</span>
              <span class="detail-value success">{{ formatNumber(selectedPackage.bonus_points) }} 点</span>
            </div>
            <div class="detail-row total-row">
              <span class="detail-label">合计资源点：</span>
              <span class="detail-value primary">{{ formatNumber(totalPoints) }} 点</span>
            </div>
          </div>
        </FormItem>
        <FormItem label="价格计算">
          <div class="price-detail">
            <div class="price-row">
              <span class="price-label">单价：</span>
              <span class="price-value">￥{{ formatPrice(selectedPackage.price) }}</span>
            </div>
            <div class="price-row">
              <span class="price-label">数量：</span>
              <span class="price-value">{{ orderForm.quantity }} 份</span>
            </div>
            <div class="price-row total-price-row">
              <span class="price-label">应付总额：</span>
              <span class="price-value total-price">￥{{ formatPrice(totalPrice) }}</span>
            </div>
          </div>
        </FormItem>
        <FormItem label="预计到期时间">
          <div class="expire-info">
            <Icon type="ios-calendar" size="18" style="margin-right: 6px;" />
            <span>{{ expectedExpireTime }}</span>
          </div>
        </FormItem>
        <FormItem>
          <Button type="primary" size="large" :loading="payModal.loading" @click="createOrder">
            <Icon type="ios-card" />
            立即支付 ￥{{ formatPrice(totalPrice) }}
          </Button>
          <Button style="margin-left: 12px;" @click="cancelOrder">取消</Button>
        </FormItem>
      </Form>
    </Card>

    <Modal
      v-model="payModal.show"
      :mask-closable="false"
      footer-hide
      class-name="vertical-center-modal"
      width="480"
      @on-cancel="closePayModal"
    >
      <template #header>
        <div style="text-align: center; font-size: 20px; font-weight: 600;">
          {{ orderForm.pay_type === 1 ? '微信扫码支付' : '支付宝扫码支付' }}
        </div>
      </template>
      <div class="pay-body">
        <vue-qr :text="payModal.code_url" :size="360" />
        <p class="tips">请使用{{ orderForm.pay_type === 1 ? '微信' : '支付宝' }}扫描二维码完成支付</p>
        <p class="tips small">支付完成后系统会自动入账资源点</p>
      </div>
    </Modal>
  </div>
</template>

<script>
import vueQr from 'vue-qr'

export default {
  name: 'AiPointsRecharge',
  components: { vueQr },
  data () {
    return {
      loading: false,
      packages: [],
      selectedPackage: null,
      orderForm: {
        quantity: 1,
        pay_type: 1 // 1微信 2支付宝
      },
      payModal: {
        show: false,
        code_url: '',
        order_sn: '',
        timer: null,
        loading: false
      },
      guideCollapse: ['0'] // 默认展开说明
    }
  },
  computed: {
    totalPoints () {
      if (!this.selectedPackage) return 0
      const basePoints = Number(this.selectedPackage.base_points || 0)
      const bonusPoints = Number(this.selectedPackage.bonus_points || 0)
      return (basePoints + bonusPoints) * this.orderForm.quantity
    },
    totalPrice () {
      if (!this.selectedPackage) return 0
      return Number(this.selectedPackage.price || 0) * this.orderForm.quantity
    },
    expectedExpireTime () {
      if (!this.selectedPackage) return '-'
      const validDays = Number(this.selectedPackage.valid_days || 365)
      const expireDate = new Date()
      expireDate.setDate(expireDate.getDate() + validDays)
      return this.formatDate(expireDate)
    }
  },
  created () {
    this.fetchAll()
  },
  beforeDestroy () {
    this.clearTimer()
  },
  methods: {
    fetchAll () {
      this.fetchPackages()
    },
    fetchPackages () {
      this.loading = true
      this.requestApi('/adm/get_package_list').then(res => {
        this.loading = false
        if (res.status === 1) {
          this.packages = res.data || []
          if (this.packages.length === 0) {
            console.warn('套餐列表为空，请检查数据库数据状态（status=1, is_deleted=0）')
          }
        } else {
          this.packages = []
          this.alertErr(res.msg || '获取套餐失败')
        }
      }).catch(err => {
        this.loading = false
        this.packages = []
        console.error('获取套餐列表失败:', err)
        this.alertErr('获取套餐列表失败，请稍后重试')
      })
    },
    selectPackage (item) {
      this.selectedPackage = { ...item }
      this.orderForm.quantity = 1
      this.orderForm.pay_type = 1
    },
    cancelOrder () {
      this.selectedPackage = null
      this.orderForm.quantity = 1
      this.orderForm.pay_type = 1
    },
    calculatePrice () {
      // 价格计算逻辑在computed中自动处理
    },
    createOrder () {
      if (!this.selectedPackage) {
        return this.alertErr('请先选择套餐')
      }
      if (this.orderForm.quantity < 1) {
        return this.alertErr('购买数量至少为1')
      }
      if (this.payModal.loading) return
      
      this.payModal.loading = true
      this.requestApi('/adm/create_ai_points_recharge_order', {
        package_id: this.selectedPackage.id,
        quantity: this.orderForm.quantity,
        pay_type: this.orderForm.pay_type
      }).then(res => {
        this.payModal.loading = false
        if (res.status === 1) {
          this.payModal.show = true
          this.payModal.code_url = res.data.code_url
          this.payModal.order_sn = res.data.order_sn
          this.startTimer()
        } else {
          this.alertErr(res.msg || '下单失败')
        }
      }).catch(() => {
        this.payModal.loading = false
      })
    },
    startTimer () {
      this.clearTimer()
      this.payModal.timer = setInterval(() => {
        this.requestApi('/adm/get_ai_points_order_status', { order_sn: this.payModal.order_sn }).then(res => {
          if (res.status === 1 && res.data.order_status === 1) {
            this.alertSucc('支付成功，资源点已入账')
            this.closePayModal()
            this.fetchAll()
          }
        })
      }, 2000)
    },
    clearTimer () {
      if (this.payModal.timer) {
        clearInterval(this.payModal.timer)
        this.payModal.timer = null
      }
    },
    closePayModal () {
      this.clearTimer()
      this.payModal.show = false
      this.payModal.code_url = ''
      this.payModal.order_sn = ''
    },
    formatPrice (value) {
      if (value === null || value === undefined) return '0.00'
      return Number(value).toFixed(2)
    },
    formatNumber (num) {
      if (num === null || num === undefined) return '0'
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    getDateTime (date) {
      if (!date) date = new Date()
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hours = String(d.getHours()).padStart(2, '0')
      const minutes = String(d.getMinutes()).padStart(2, '0')
      const seconds = String(d.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    formatDate (date) {
      if (!date) return '-'
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hours = String(d.getHours()).padStart(2, '0')
      const minutes = String(d.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 16px;
  background: #f5f7f9;
  min-height: calc(100vh - 64px);
}

/* 使用说明区域 */
.usage-guide-section {
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.guide-panel-title {
  font-weight: 600;
  color: #17233d;
}

.guide-content {
  padding: 8px 0;
}

.guide-item {
  margin-bottom: 24px;
}

.guide-item:last-child {
  margin-bottom: 0;
}

.guide-item-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #2d8cf0;
  margin-bottom: 12px;
}

.guide-list {
  margin: 0;
  padding-left: 24px;
  line-height: 1.8;
}

.guide-list li {
  margin-bottom: 8px;
  color: #515a6e;
  font-size: 14px;
}

.guide-list li:last-child {
  margin-bottom: 0;
}

.guide-list strong {
  color: #17233d;
  font-weight: 600;
}

.guide-example {
  margin-top: 20px;
  padding: 16px;
  background: linear-gradient(135deg, #f0f7ff 0%, #e6f4ff 100%);
  border-radius: 8px;
  border-left: 4px solid #2d8cf0;
}

.example-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #2d8cf0;
  margin-bottom: 12px;
}

.example-content {
  color: #515a6e;
  font-size: 14px;
  line-height: 1.8;
}

.example-content p {
  margin-bottom: 8px;
}

.example-content p:last-child {
  margin-bottom: 0;
}

.example-calculation {
  margin-top: 12px;
  padding: 12px;
  background: #fff;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  color: #17233d;
  font-weight: 500;
  border: 1px solid #d4e8ff;
}

/* 套餐区域 */
.package-section {
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
}

.package-card {
  border: 1px solid #e8eaec;
  border-radius: 12px;
  padding: 0;
  margin-bottom: 16px;
  transition: all 0.3s ease;
  background: #fff;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.package-card:hover {
  border-color: #2d8cf0;
}

.package-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #2d8cf0 0%, #5cadff 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}


.package-card:hover::before {
  opacity: 1;
}

.package-card-selected {
  border-color: #2d8cf0;
  border-width: 1px;
  background: linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%);
  box-shadow: 0 6px 20px rgba(45, 140, 240, 0.25);
}

.package-card-selected::before {
  opacity: 1;
}

.package-selected-badge {
  position: absolute;
  top: 0;
  right: 0;
  background: linear-gradient(135deg, #19be6b 0%, #2d8cf0 100%);
  color: #fff;
  padding: 4px 12px;
  border-radius: 0 12px 0 12px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 1;
  box-shadow: 0 2px 6px rgba(25, 190, 107, 0.3);
}

.package-content {
  padding: 20px 16px;
  text-align: center;
}

.package-name {
  font-size: 16px;
  font-weight: 600;
  color: #17233d;
  margin-bottom: 16px;
  line-height: 1.4;
}

.package-price-wrapper {
  margin-bottom: 16px;
  line-height: 1;
  display: flex;
  align-items: baseline;
  justify-content: center;
}

.price-symbol {
  font-size: 20px;
  color: #ed4014;
  font-weight: 600;
  margin-right: 2px;
  line-height: 1;
}

.price-value {
  font-size: 36px;
  color: #ed4014;
  font-weight: 700;
  line-height: 1;
}

.package-points-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-bottom: 12px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #f0f7ff 0%, #e6f4ff 100%);
  border-radius: 8px;
}

.points-label {
  font-size: 22px;
  font-weight: 700;
  color: #2d8cf0;
  line-height: 1;
}

.points-unit {
  font-size: 13px;
  color: #808695;
  font-weight: 500;
}

.package-validity {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 12px;
  color: #808695;
  padding-top: 8px;
  border-top: 1px dashed #e8eaec;
}


/* 订单详情区域 */
.order-section {
  margin-top: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.selected-package-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.package-name {
  font-size: 16px;
  font-weight: 600;
  color: #17233d;
}

.package-desc {
  font-size: 13px;
  color: #808695;
}

.points-detail {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 6px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.total-row {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e8eaec;
  font-weight: 600;
}

.detail-label {
  color: #808695;
}

.detail-value {
  color: #17233d;
  font-weight: 500;
}

.detail-value.success {
  color: #19be6b;
}

.detail-value.primary {
  color: #2d8cf0;
  font-size: 16px;
}

.price-detail {
  background: #fff7e6;
  padding: 16px;
  border-radius: 6px;
  border: 1px solid #ffe58f;
}

.price-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
}

.price-row:last-child {
  margin-bottom: 0;
}

.total-price-row {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #ffe58f;
  font-weight: 600;
}

.price-label {
  color: #808695;
}

.price-value {
  color: #17233d;
  font-weight: 500;
}

.total-price {
  color: #ed4014;
  font-size: 20px;
}

.expire-info {
  display: flex;
  align-items: center;
  font-size: 15px;
  color: #17233d;
  font-weight: 500;
}

/* 支付弹窗 */
.pay-body {
  text-align: center;
  padding: 20px;
}

.tips {
  margin-top: 16px;
  color: #515a6e;
  font-size: 14px;
}

.tips.small {
  font-size: 12px;
  color: #808695;
  margin-top: 8px;
}
</style>

